import CardServiceIconV2 from 'assets/Icons/cardServiceIconV2';
import Close from 'assets/Icons/close';
import IcDashboard from 'assets/Icons/IcDashboard';
import IcMarketing from 'assets/Icons/IcMarketing';
import IcMoney from 'assets/Icons/IcMoney';
import IcNumberKeyboard from 'assets/Icons/IcNumberKeyboard';
import IcOnline from 'assets/Icons/IcOnline';
import IcReferral from 'assets/Icons/IcReferral';
import Box from 'components/Box';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';

type ContactUsModalProps = {};

type ContactUsModalRef = {
  open: () => void;
};

export const useContactUsModalRef = () => useRef<ContactUsModalRef>(null);

const ContactUsModal = forwardRef<ContactUsModalRef, ContactUsModalProps>((_props, ref) => {
  const [visible, setVisible] = useState(false);

  const handleClose = () => {
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    open: () => {
      setVisible(true);
    },
  }));

  return (
    <StyledModal
      modalTitle='CONTACT US'
      visible={visible}
      onClose={handleClose}
      v2
      width={900}
      noneBodyStyle
      destroyOnClose
      footer={null}
      showClose
      closeIconCustom={
        <CloseIcon>
          <Close fill='#FFF'/>
        </CloseIcon>
      }
    >
      <Container>
        <QuickService>
          <Box display='flex' flexDirection='column' gap='1'>
            <Text variant='H8' color='v2_secondary_3' className='text-medium'>
              Team Leader: A.Toàn
            </Text>
            <Text variant='H5'>Quick service</Text>
          </Box>
          <Text variant='H2'>(904) 444-3333</Text>
        </QuickService>
        <QuickService>
          <Box display='flex' alignItems='center' gap='2'>
            <Text variant='H5'>Team Support</Text>
            <Language>
              <Text variant='H6' color='v2_primary_bg_6' className='text-medium'>
                English
              </Text>
            </Language>
          </Box>
          <Text variant='H2'>(888) 449-1592</Text>
        </QuickService>
        <TeamSupport>
          <Header>
            <Box display='flex' alignItems='center' gap='2'>
              <Text variant='H5'>Team Support</Text>
              <Language>
                <Text variant='H6' color='v2_primary_bg_6' className='text-medium'>
                  Vietnamese
                </Text>
              </Language>
            </Box>
            <Text variant='H2'>(800) 912-0503</Text>
          </Header>
          <Department>
            <Box display='flex' gap='3' alignItems='center'>
              <IcNumberKeyboard />
              <Text variant='H6' className='text-medium'>
                Press a number to reach the department
              </Text>
            </Box>
            <ServiceDepartment>
              <ServiceItem
                icon={<CardServiceIconV2 width={28} height={28} fill='#2D6772' />}
                title='For Customer Service'
                number='0'
                bgColor='#E4F4F6'
              />
              <ServiceItem
                icon={<IcMoney width={28} height={28} fill='#0C865D' />}
                title='For Sale'
                number='1'
                bgColor='#EFFFFA'
              />
              <ServiceItem
                icon={<IcMarketing width={28} height={28} fill='#4B63C7' />}
                title='For Marketing'
                number='2'
                bgColor='#EEF4FF'
              />
              <ServiceItem
                icon={<IcOnline width={28} height={28} fill='#D2464F' />}
                title='For Website Service'
                number='3'
                bgColor='#FFF6F9'
              />
              <ServiceItem
                icon={<IcDashboard width={28} height={28} fill='#C52884' />}
                title='For Digital Signage'
                number='4'
                bgColor='#FFEAF6'
              />
              <ServiceItem
                icon={<IcReferral width={28} height={28} fill='#FF7D00' />}
                title='For Agents & Partnerships'
                number='5'
                bgColor='#FFFAF8'
              />
            </ServiceDepartment>
          </Department>
        </TeamSupport>
        <QuickService>
          <Text variant='H5'>Credit card processing 24/7</Text>
          <Text variant='H2'>(800) 789-8897</Text>
        </QuickService>
      </Container>
    </StyledModal>
  );
});

ContactUsModal.displayName = 'ContactUsModal';
export default ContactUsModal;

const StyledModal = styled(Modal)`
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const CloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #fff;
  border-radius: 4px;
  padding: 2px;

  &:hover {
    opacity: 0.8;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 24px 32px 24px;
  gap: 16px;
  font-family: 'Poppins';

  .text-medium {
    font-weight: 500;
  }
`;

const QuickService = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-radius: 12px;
  border: 1px solid #a8dee8;
  background: #f2feff;
`;

const TeamSupport = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #a8dee8;
  border-radius: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f2feff;
  padding: 16px 24px;
  border-radius: 12px;
`;

const Department = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  gap: 16px;
`;

const ServiceDepartment = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 1px solid #e9edf2;
  background: #fff;
`;

const Language = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 4px;
  background: #FFE5E7;
`;

interface ServiceItemProps {
  icon: React.ReactNode;
  title: string;
  number: string;
  bgColor?: string;
}

const ServiceItem = ({ icon, title, number, bgColor }: ServiceItemProps) => {
  return (
    <ServiceItemContainer>
      <Box display='flex' alignItems='center' gap='3'>
        <Box
          className='box-icon'
          display='flex'
          justifyContent='center'
          alignItems='center'
          style={{ background: bgColor || '#F2FEFF' }}
        >
          {icon}
        </Box>
        <Text variant='H6' className='text-medium' color='text_4_v2'>
          {title}
        </Text>
      </Box>
      <Text variant='H2'>{number}</Text>
    </ServiceItemContainer>
  );
};

const ServiceItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #E9EDF2;

  .box-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
  }

  &:hover {
    background: #f2feff;
    cursor: pointer;
  }

  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

    &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;
